/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnDestroy,
	OnInit,
	Optional,
	Output,
} from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	UntypedFormBuilder,
	UntypedFormGroup,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	ValidatorFn,
	FormGroupDirective,
	Validators,
} from '@angular/forms';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import * as appStrings from '@touchplan/strings';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService } from '@touchplan/logging';
import { MatSelect } from '@angular/material/select';

export type OptionCollection = string[] | number[] | Map<string, any>;

export class OptionData {
	public label: string;
	public displayedLabel: string;
	public matchesFilter: boolean;
	public iconColor: string;
}

@Component({
	selector: 'mat-extended-select',
	templateUrl: './mat-extended-select.component.html',
	styleUrls: ['./mat-extended-select.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MatExtendedSelectComponent),
			multi: true,
		},
		LoggerServiceFactory.provide('MatExtendedSelectComponent'),
	],
})
export class MatExtendedSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
	public strings = appStrings;

	public formGroup: UntypedFormGroup;

	@Input() public extClass = 'full-width';
	@Input() public placeholder: string;
	@Input() public labelToDisplay: string;
	@Input() public searchPlaceholder = appStrings.UICONTROL_TEXT_SEARCH_NO_ELLIPSIS;
	@Input() public option$: Observable<OptionCollection>;
	@Input() public required = true;
	@Input() public includeNotSetOption = false;
	@Input() public subScriptSizing: 'fixed' | 'dynamic' = 'fixed';
	@Input() public hint?: string = null;
	@Input() public notSetLabel = appStrings.UICONTROL_TEXT_NOT_SET;
	@Input() public notSetValue?: string = null;
	@Input() public isNotSetValid = false;
	@Input() public includeFilter = true;
	@Input() public multiSelect = false;
	@Input() public sortOptions = true;
	@Input() public labelPath?: string[] = null;
	@Input() public uniqueLabelFunction?: (
		label: string,
		mapKey: string,
		mapValue: unknown,
		count: number
	) => string;
	@Input() public valuePath?: string[] = null;
	@Input() public sortCompareFunction = this.defaultCompareFunction;
	@Input() public customSelectionSummaryFunction?: (
		mapKeys: string[],
		displayedLabels: string[],
		selectedMapValue: any
	) => string;
	@Input() public noSelectionText?: string;
	@Input() public getIconBackgroundColor: (label: string, value: any) => string = null;
	@Input() public iconBackgroundColorPath: string[] = null;
	@Input() public showSelectAllCheckbox = true;

	/**
	 * Event emitter that triggers when the dropdown is closed.
	 * Emits a void event to notify parent components.
	 *
	 * @see handleFilterMultiSelectClosed() - Method that triggers this event.
	 */
	@Output() public closed = new EventEmitter<void>();

	/*
	 * In the future, these might be used to support nested/<ng-content> style <mat-option>s provided
	 * by <mat-extended-select> users.  For now, we're punting since we don't know how to attach
	 * such user-provided options to this component's <mat-autocomplete> or <mat-select>.
	 * Trying an <ng-content>-style approach seems to provide the <mat-option>s "too late",
	 * but more investigation is needed.
	 *
	@ContentChildren(MatOption, {descendants: true}) options: QueryList<MatOption>;
	@ViewChild('matAutocomplete') public matAutocomplete;
	@ViewChild('matSelect1') public matSelect1;
	@ViewChild('matSelect2') public matSelect2;
	*/

	public allOption$: Observable<OptionData[]>;
	public allOptionsSync: OptionData[];

	// Once the dropdown has been populated, this is non-null and has the SORTED labels:
	public optionDataUnfilteredSync: OptionData[];

	// Once the dropdown has been populated, this is non-null ONLY if a map was yielded for the options:
	public optionMapUnfilteredSync: Map<string, any>;

	public selection: string | string[]; // depending on "multiSelect"
	public selectedMapValue: any | any[]; // non-null if the option$ Observable yielded a Map<string,any>; an array on multi-select

	// A unique, internal-to-this-class object used internally only as the value, when <not set> is selected.
	// In particular, say (for some odd reason) the user set notSetLabel to “Uni”.  Selecting that will make
	// the filter text “Uni”. Now - should the implementation then display only United States and United Kingdom,
	// because those are the only countries that match that? How does the code realize, “Oh no, that’s the ‘not set’
	// text; don’t ACTUALLY use that as a filter…”? That’s the main problem solved by notSet_internalValue.
	public notSet_internalValue = { notSeenByCaller: true };

	public overridablePlaceholder: string;

	private destroy$ = new Subject<void>();

	private _disabled = false; // See associated getter and setter, and setDisabledState

	private _invalidSelectionObject = { custom: appStrings.UICONTROL_TEXT_INVALID_SELECTION };

	// Field to support ControlValueAccessor functionality
	private onChange: any = () => {};

	constructor(
		private builder: UntypedFormBuilder,
		private logger: DefaultLoggerService,
		@Optional() private controlContainer: FormGroupDirective
	) {}

	// These just help make other code more concise:
	protected get filterTextControl(): AbstractControl {
		return this.formGroup.controls['filterTextControl'];
	}
	protected get filterMultiSelectTextControl(): AbstractControl {
		return this.formGroup.controls['filterMultiSelectTextControl'];
	}
	protected get matSelectControl(): AbstractControl {
		return this.formGroup.controls['matSelectControl'];
	}
	protected get matMultiSelectControl(): AbstractControl {
		return this.formGroup.controls['matMultiSelectControl'];
	}
	protected get matFilterMultiSelectControl(): AbstractControl {
		return this.formGroup.controls['matFilterMultiSelectControl'];
	}
	protected get selectAllChecked(): AbstractControl {
		return this.formGroup.controls['selectAllChecked'];
	}

	private get activeFormControl(): AbstractControl {
		if (this.includeFilter) {
			return this.multiSelect ? this.matFilterMultiSelectControl : this.filterTextControl;
		}
		return this.multiSelect ? this.matMultiSelectControl : this.matSelectControl;
	}

	public get disabled(): boolean {
		return this._disabled;
	}

	/**
	 * Be aware, setting this value will always be interpreted as a "system" change and not a "user" change.
	 * Thus, setting this value directly should probably be considered a mistake.
	 */
	public set disabled(dis: boolean) {
		if (this._disabled === dis) {
			return;
		}
		this._disabled = dis;
		if (this.formGroup) {
			if (dis) {
				this.formGroup.disable({ emitEvent: false });
			} else {
				this.formGroup.enable({ emitEvent: false });
			}
		}
	}

	public ngOnInit(): void {
		this.formGroup = this.builder.group({
			filterTextControl: [{ value: '', disabled: this.disabled }],
			filterMultiSelectTextControl: [{ value: '', disabled: this.disabled }],
			matSelectControl: [{ value: '', disabled: this.disabled }],
			matMultiSelectControl: [{ value: '', disabled: this.disabled }],
			matFilterMultiSelectControl: [{ value: '', disabled: this.disabled }],
			selectAllChecked: [{ value: false, disabled: this.disabled }],
		});

		if (!this.option$) {
			this.logger.error('option$ Observable is ' + this.option$);
			return;
		}

		this.overridablePlaceholder = this.placeholder;

		// If [labelPath] is a ('.'-separated) string, convert it to a string[]:
		if (this.labelPath && typeof this.labelPath === 'string') {
			this.labelPath = (this.labelPath as string).split('.');
		}

		// Create an Observable to:
		//
		// - Keep "optionStringsUnfilteredSync" (string[]) up-to-date
		//     - ... and sort it if necessary
		// - Keep "optionMapUnfilteredSync" (Map<string, any>) up-to-date, if a Map is yielded
		//
		const sortedOptionData$ = this.option$.pipe(
			map((arrayOrMap) => {
				let result: OptionData[];
				if (Array.isArray(arrayOrMap)) {
					const labels = arrayOrMap as string[];
					result = labels.map(
						(label) => ({ label, displayedLabel: label, matchesFilter: true, iconColor: null } as OptionData)
					);
					this.optionMapUnfilteredSync = null;
				} else {
					// Options are provided as a Map<string,any>
					this.optionMapUnfilteredSync = arrayOrMap as Map<string, any>;
					result = this.getUnsortedLabelsFromMap(this.optionMapUnfilteredSync);
				}
				// Sort if required:
				if (this.sortOptions) {
					result = result.sort((optionData1, optionData2) =>
						this.sortCompareFunction(optionData1.displayedLabel, optionData2.displayedLabel)
					);
				}
				this.optionDataUnfilteredSync = result;
				return result;
			}),
			takeUntil(this.destroy$)
		);

		// Merge the sorted options with the filter text (if any):
		this.allOption$ = combineLatest([
			sortedOptionData$,
			this.multiSelect
				? this.filterMultiSelectTextControl.valueChanges.pipe(startWith(''))
				: this.filterTextControl.valueChanges.pipe(startWith('')),
		]).pipe(
			map((pair) => {
				const sortedOptionData = pair[0] ? pair[0] : [];
				let filterText: string;
				if (
					pair[1] === undefined ||
					pair[1] === null ||
					pair[1] === '' ||
					pair[1] === this.notSet_internalValue ||
					pair[1] === this.notSetLabel
				) {
					filterText = '';
				} else if (this.labelPath && !this.multiSelect) {
					filterText = this.keyToLabelViaLabelPath(pair[1], pair[1]);
				} else {
					filterText = pair[1];
				}

				// Filter if required:
				const allOptions = [] as OptionData[];
				if (
					this.includeFilter &&
					filterText.length > 0 &&
					!sortedOptionData.map((obj) => obj.displayedLabel).includes(filterText)
				) {
					// so dropdown doesn't show only one element when existing choice is valid
					const filterTextLC = filterText.toLowerCase();
					for (const optionData of sortedOptionData) {
						allOptions.push({
							label: optionData.label,
							displayedLabel: optionData.displayedLabel,
							matchesFilter: optionData.displayedLabel.toLowerCase().includes(filterTextLC),
							iconColor: this.hasIconColor() ? this.getIconColor(optionData) : null,
						});
					}
				} else {
					// No filter
					for (const optionData of sortedOptionData) {
						allOptions.push({
							label: optionData.label,
							displayedLabel: optionData.displayedLabel,
							matchesFilter: true,
							iconColor: this.hasIconColor() ? this.getIconColor(optionData) : null,
						});
					}
				}
				return allOptions;
			}),
			takeUntil(this.destroy$)
		) as Observable<OptionData[]>;

		// Hang on to the most recent OptionData[]:
		this.allOption$.pipe(takeUntil(this.destroy$)).subscribe((options) => (this.allOptionsSync = options));

		// Listen to and forward single-select FormControl changes:
		if (!this.multiSelect) {
			for (const singleSelectFormControl of [this.filterTextControl, this.matSelectControl]) {
				singleSelectFormControl.valueChanges
					.pipe(
						filter((val) => this.reportSingleSelectValue(val)),
						takeUntil(this.destroy$)
					)
					.subscribe((val) => {
						this.reportNewValue([val]);
					});
			}
		}

		// Listen to and forward multi-select FormControl changes:
		if (this.multiSelect) {
			for (const multiSelectFormControl of [this.matMultiSelectControl, this.matFilterMultiSelectControl]) {
				multiSelectFormControl.valueChanges
					.pipe(
						filter((val) => val !== undefined && val !== null),
						takeUntil(this.destroy$)
					)
					.subscribe((val) => {
						const resultArray = [];
						for (const text of val) {
							resultArray.push(text);
						}
						this.reportNewValue(resultArray);
					});
			}
		}

		// Handle validation as needed:
		if (this.required) {
			if (this.multiSelect) {
				const selectionValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
					const values = control.value;
					return values && Array.isArray(values) && values.length > 0 ? null : this._invalidSelectionObject;
				};
				this.activeFormControl.setValidators(selectionValidator);
				this.activeFormControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
			} else {
				// Single-select:
				const selectionValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
					// Check that the text in the filter text box is a valid option:
					let nameInTextBox = control.value;
					if (
						this.includeNotSetOption &&
						(nameInTextBox === this.notSetLabel || nameInTextBox === this.notSet_internalValue)
					) {
						// The <not set> option is selected:
						return this.isNotSetValid ? null : this._invalidSelectionObject;
					}
					if (nameInTextBox && this.labelPath && !this.includeFilter) {
						nameInTextBox = this.keyToLabelViaLabelPath(nameInTextBox);
					}
					return this.optionDataUnfilteredSync?.map((elem) => elem.displayedLabel).includes(nameInTextBox)
						? null
						: this._invalidSelectionObject;
				};
				this.activeFormControl.addValidators(selectionValidator);
				this.activeFormControl.addValidators(Validators.required);
				this.activeFormControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
			}
		}

		if (!!this.valuePath && typeof this.valuePath === 'string') {
			// Be nice and fix this for the user:
			this.valuePath = [this.valuePath];
		}

		this.updateLabelToDisplay();
		this.parentFormSubmitEventSubscription();
	}

	private getUnsortedLabelsFromMap(inputMap: Map<string, any>): OptionData[] {
		const result = [] as OptionData[];
		const displayedLabelCounts = new Map<string, number>();
		let thereAreDuplicates = false;
		for (const [key, value] of inputMap.entries()) {
			const displayedLabel = this.labelPath ? this.getFieldAtPath(value, this.labelPath, key) : key;
			result.push({ label: key, displayedLabel, matchesFilter: true, iconColor: null });
			// Update the count for this "displayedLabel"
			const count = displayedLabelCounts.has(displayedLabel) ? displayedLabelCounts.get(displayedLabel) : 0;
			thereAreDuplicates = thereAreDuplicates || count === 1;
			displayedLabelCounts.set(displayedLabel, count + 1);
		}

		// If the user provided a "[uniqueLabelFunction]", then call it for any duplicated "displayedLabel"s:
		if (thereAreDuplicates && this.uniqueLabelFunction) {
			const countsForCalls = new Map<string, number>();
			for (const optionData of result) {
				const displayedLabel = optionData.displayedLabel;
				const count = displayedLabelCounts.get(displayedLabel);
				if (count > 1) {
					const c = countsForCalls.has(displayedLabel) ? countsForCalls.get(displayedLabel) : 1;
					countsForCalls.set(displayedLabel, c + 1);
					const mapKey = optionData.label;
					const newDisplayedLabel = this.uniqueLabelFunction(displayedLabel, mapKey, inputMap.get(mapKey), c);
					optionData.displayedLabel = newDisplayedLabel;
				}
			}
		}
		return result;
	}

	private getFieldAtPath(obj: any, path: string[], defaultValue: any) {
		let value = obj;
		if (!obj) {
			return defaultValue;
		}
		for (const pathElem of path) {
			value = value[pathElem];
			if (!value) {
				return defaultValue;
			}
		}
		return value;
	}

	private reportSingleSelectValue(val: any) {
		if (!val) {
			return false;
		}
		if (this.optionDataUnfilteredSync.map((elem) => elem.label).includes(val)) {
			return true;
		}
		return (
			this.includeNotSetOption &&
			(val === this.notSet_internalValue || val === appStrings.UICONTROL_TEXT_NOT_SET)
		);
	}

	public handleFilterTextClick(filterText: HTMLElement): void {
		// Display the dropdown menu.
		//
		// If anybody knows of an easier way to programmatically display the
		// dropdown menu, be my guest:
		filterText.blur();
		setTimeout(() => {
			filterText.focus();
		}, 100);
	}

	public handleFilterMultiSelectOpenedChange(selectControl: MatSelect) {
		if (selectControl.panelOpen) {
			this.makeSureDropdownIsScrolledToTheTop(selectControl);
		} else {
			this.handleFilterMultiSelectClose();
		}
	}

	public handleFilteredMultiSelectClick(selectControl: MatSelect, filterInput: HTMLElement) {
		this.makeSureDropdownIsScrolledToTheTop(selectControl);
		// Put the cursor into the filter input box:
		setTimeout(() => {
			filterInput.focus();
		}, 100);
	}

	private makeSureDropdownIsScrolledToTheTop(selectControl: MatSelect) {
		setTimeout(() => {
			// Ater a short wait, scroll to the top, since
			// MatSelect display code wants to auto-scroll down to the selected
			// option(s), which we explicitly want to override in order to make
			// sure the first filter-box option is made visible.
			//
			// We want the wait to be very short (50 ms) so that the user doesn't
			// actually see any change in the scroll position:
			if (selectControl?.panel?.nativeElement) {
				selectControl.panel.nativeElement.scrollTop = 0;
			}
		}, 50);
		// Twist that frickin' arm.
	}

	private handleFilterMultiSelectClose() {
		// Clear the filter:
		this.filterMultiSelectTextControl.setValue('');
		// Clear the "select-all" checkbox:
		this.selectAllChecked.setValue(false);
	}

	public toggleSelectAll(val: any) {
		// Initially the new selection set is the same as the old one:
		let newSelectionSet: string[];

		if (val.checked) {
			// Initially the new selection set is the same as the old one:
			newSelectionSet = this.selection ? (this.selection as string[]).slice() : [];

			// Add those that match the filter that aren't already in the selection set:
			for (const option of this.allOptionsSync) {
				if (option.matchesFilter && !newSelectionSet.includes(option.label)) {
					if (this.usesValuePath()) {
						const valueAtPath = this.lookupUsingKeyAndValuePath(option.label, this.valuePath);
						newSelectionSet.push(valueAtPath);
					} else {
						newSelectionSet.push(option.label);
					}
				}
			}
		} else {
			// Deselecting those that match the filter:
			newSelectionSet = [];
			for (const oldSelection of this.selection) {
				// Exclude if this selection now matches the current filter:
				const match = this.allOptionsSync.find((opt) => opt.label === oldSelection);
				if (match && !match.matchesFilter) {
					newSelectionSet.push(oldSelection);
				}
			}
		}
		this.writeValue(newSelectionSet);
	}

	private defaultCompareFunction(a: string, b: string): number {
		return a.localeCompare(b);
	}

	public selectionSummaryText() {
		const mapKeys = this.selection ? (this.selection as string[]) : ([] as string[]);
		// Translate the Map keys to the corresponding "displayedLabel"s:
		const displayedLabels = [] as string[];
		for (const mapKey of mapKeys) {
			const opt = this.allOptionsSync.find((elem) => elem.label === mapKey);
			displayedLabels.push(opt ? opt.displayedLabel : mapKey);
		}

		if (this.customSelectionSummaryFunction) {
			return this.customSelectionSummaryFunction(mapKeys, displayedLabels, this.selectedMapValue);
		}
		return displayedLabels.join(', ');
	}

	private usesValuePath(): boolean {
		// "valuePath" is only valid when a Map is used to populate this component;
		return !!this.valuePath && !!this.optionMapUnfilteredSync;
	}

	private setSelection(value: any): void {
		this.selection = value;
		this.updateLabelToDisplay();
	}

	private updateLabelToDisplay() {
		this.overridablePlaceholder = this.placeholder;
		if (!this.selection || this.selection.length === 0) {
			this.labelToDisplay = this.noSelectionText ? this.noSelectionText : this.placeholder;
		} else {
			const selectedKey = Array.isArray(this.selection) ? this.selection[0] : this.selection;
			if (this.includeNotSetOption && this.notSetValue === selectedKey) {
				if (this.includeFilter) {
					this.filterTextControl.setValue(this.notSetLabel, { emitEvent: false });
				} else {
					this.overridablePlaceholder = this.notSetLabel;
				}
			} else if (this.labelPath && this.includeFilter && !this.multiSelect) {
				const summaryToDisplay = this.keyToLabelViaLabelPath(selectedKey);
				this.filterTextControl.setValue(summaryToDisplay, { emitEvent: false });
			}
			this.labelToDisplay = this.placeholder;
		}
	}

	private keyToLabelViaLabelPath(key: string, defaultValue?: string) {
		const mapValue = this.optionMapUnfilteredSync.get(key);
		if (!mapValue) {
			return defaultValue;
		}
		return this.getFieldAtPath(mapValue, this.labelPath, defaultValue);
	}

	private reportNewValue(values: any[]): void {
		let notSetIsSelected = false;
		if (values.length === 1 && this.includeNotSetOption) {
			if (values[0] === this.notSetLabel) {
				notSetIsSelected = true;
				values[0] = this.notSetValue;
			} else if (values[0] === this.notSet_internalValue) {
				notSetIsSelected = true;
				values[0] = this.notSetValue;
				this.updateControlValue(this.notSetLabel);
			}
		}

		// If the value hasn't actually changed, don't report anything and just return instead:
		const selArray = Array.isArray(this.selection)
			? (this.selection as string[])
			: [this.selection as string];
		if (this.equalValues(selArray, values)) {
			/* TP-4373: still need to do this when [labelPath] is used, since the label is different than the displayed label */
			if (this.labelPath) {
				this.setSelection(values);
				this.updateLabelToDisplay();
			}
			return;
		}
		if (notSetIsSelected) {
			this.setSelection(values);
			this.selectedMapValue = this.notSetValue;
			this.onChange(values[0]);
			return;
		}

		this.setSelection(values);
		this.updateSelectedMapValue(values);

		// Now "report" the selection(s) to what is bound to this <mat-extended-select>s FormControl.
		// Depending on the "valuePath" option, we either report the values (which are dropdown option strings)
		// or - if a Map was used to populate this component - we report using the value(s) for the selected
		// dropdown options:
		if (!this.usesValuePath()) {
			// No "valuePath", so report the dropdown options' strings:
			this.onChange(this.multiSelect ? values : values[0]);
		} else {
			if (!this.multiSelect) {
				// Single-select case:
				const mapValue = this.lookupUsingKeyAndValuePath(values[0], this.valuePath);
				this.onChange(mapValue === this.notSetLabel ? this.notSetValue : mapValue);
			} else {
				// Multi-select case:
				const mappedValues = [];
				for (const val of values) {
					mappedValues.push(this.lookupUsingKeyAndValuePath(val, this.valuePath));
				}
				this.onChange(mappedValues);
			}
		}
	}

	private updateControlValue(value: any): void {
		this.activeFormControl.setValue(value);
	}

	private equalValues(v1: string[], v2: string[]): boolean {
		if (!v1) {
			return !v2;
		}
		// !!v1 = true
		if (!v2) {
			return false;
		}
		if (v1.length !== v2.length) {
			return false;
		}
		for (let i = 0; i < v1.length; i++) {
			if (v1[i] !== v2[i]) {
				return false;
			}
		}
		return true;
	}

	private updateSelectedMapValue(values: string[]): void {
		if (this.optionMapUnfilteredSync) {
			if (this.multiSelect) {
				this.selectedMapValue = [];
				for (const val of values as string[]) {
					this.selectedMapValue.push(this.optionMapUnfilteredSync.get(val));
				}
			} else {
				this.selectedMapValue = this.optionMapUnfilteredSync.get(values[0]);
			}
		} else {
			this.selectedMapValue = null;
		}
	}

	// A method required in order to implement the ControlValueAccessor interface.
	// This is called by Angular to set the initial value of this component.
	public writeValue(value: any): void {
		if (this.multiSelect && (value === null || value === undefined)) {
			value = []; // Do the user a favor
		}
		if (this.includeNotSetOption && this.notSetValue === value) {
			this.updateControlValue(this.notSetLabel);
			this.setSelection([this.notSetValue]);
			this.selectedMapValue = this.notSetValue;
			return;
		}
		if (value === null || value === undefined) {
			this.setSelection(value);
			this.updateControlValue(value);
			return;
		}

		// Depending on the "valuePath" option, we either treat the value as a dropdown option string,
		// or - if a Map was used to populate this component - we treat the value(s) as being Map values
		// or inside Map values:
		if (!this.valuePath) {
			// No "valuePath"; treat "value" as a dropdown options' string(s):
			if (value) {
				this.setSelection(value);
				this.updateControlValue(value);
			}
		} else {
			// Find the Map value(s) that correspond to the initial value(s) given:
			if (!this.multiSelect || !Array.isArray(value)) {
				// Single-value case:
				const key = this.findKeyForMapValueAtPath(value, this.valuePath);
				if (key) {
					this.setSelection(key);
					this.updateControlValue(key);
				}
			} else {
				// Multi-select case:
				const values = value as string[];
				const keys = [];
				for (const v of values) {
					const k = this.findKeyForMapValueAtPath(v, this.valuePath);
					if (k) {
						keys.push(k);
					}
				}
				this.reportNewValue(keys);
				this.updateControlValue(keys);
			}
		}
	}

	private findKeyForMapValueAtPath(valueToMatch: any, valPath: string[]): string {
		for (const [key, value] of this.optionMapUnfilteredSync) {
			if (valueToMatch === this.lookupUsingKeyAndValuePath(key, valPath)) {
				return key;
			}
		}
		return null;
	}

	/**
	 * Using the Map used to populate this component's dropdown, this uses "mapKey" as a map key to get the
	 * mapped value.  Then the given "valPath" is used to get at a sub-field of that value.  If "valPath"
	 * is an empty array, the map value is just returned.  If "valPath" has a single element ("foo", say),
	 * then the field named "foo" in the map value is returned.  If "valPath" contains "foo" and "bar", then
	 * "{mapValue}.foo.bar" is returned.  And so on...
	 *
	 * If "valPath" contains a path element for which there is no corresponding field, then a warning is
	 * written to the logger and the returned value defaults to the given "mapKey".
	 */
	private lookupUsingKeyAndValuePath(mapKey: string, valPath: string[]): any {
		if (!this.optionMapUnfilteredSync.has(mapKey)) {
			return mapKey;
		}
		const mapValue = this.optionMapUnfilteredSync.get(mapKey);
		let value = mapValue;
		for (const pathElem of valPath) {
			if (!(pathElem in value)) {
				this.logger.warn(
					"[valuePath] invalid for Map element at key = '" + mapKey + "'; valPath = ",
					valPath
				);
				this.logger.warn('    Map value: ', mapValue);
				return mapKey;
			}
			value = value[pathElem];
		}
		return value;
	}

	public hasIconColor(): boolean {
		return !!this.getIconBackgroundColor || !!this.iconBackgroundColorPath;
	}

	private getIconColor(option: OptionData): string {
		const defaultColor = 'black';
		if (!!this.iconBackgroundColorPath) {
			if (typeof this.iconBackgroundColorPath === 'string') {
				// Be nice and fix this for the user:
				this.iconBackgroundColorPath = [this.iconBackgroundColorPath];
			}
			if (!this.optionMapUnfilteredSync) {
				return defaultColor;
			}
			let value = this.optionMapUnfilteredSync.get(option.label);
			for (const pathElem of this.iconBackgroundColorPath) {
				value = value[pathElem];
				if (!value) {
					return defaultColor;
				}
			}
			return value;
		}
		if (!!this.getIconBackgroundColor) {
			const result = this.getIconBackgroundColor(
				option.label,
				this.optionMapUnfilteredSync?.get(option.label)
			);
			return result ? result : defaultColor;
		}
		return defaultColor;
	}

	// A method required in order to implement the ControlValueAccessor interface
	public registerOnChange(fn: (thing: any) => void): void {
		this.onChange = fn;
	}

	// A method required in order to implement the ControlValueAccessor interface
	public registerOnTouched(fn: () => void): void {
		// We don't particularly care about the given "fn"
	}

	// A method of the ControlValueAccessor interface
	public setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	// Handles closing the dropdown when the user clicks outside.
	// Ideally, this would use the 'blur' event (e.g., updateOn: 'blur' for a FormControl),
	// but since this component is not able to emit the blur event (probably because we forgot to include the code to enable it or we did but it didn't work),
	// we use this custom method based on the mat-select closed event.
	public handleFilterMultiSelectClosed() {
		this.closed.emit();
	}

	// A method that subscribes to the parent form's submit event
	private parentFormSubmitEventSubscription(): void {
		this.controlContainer?.ngSubmit.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.formGroup.markAllAsTouched();
		});
	}

	public ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
